import { useEffect, useRef, useState } from 'react';
import { useStore, type TerminalLine } from '../state/store';
import { startSerialMonitor, stopSerialMonitor, getSerialSession, SERIAL_BAUD_OPTIONS } from '../flash/serial-port';
import { IconTrash, IconChevronDown, IconTerminal } from './icons';

const PHASE_LABELS: Record<string, string> = {
  idle: 'idle',
  generating: 'generating C++',
  compiling: 'compiling',
  flashing: 'flashing',
  monitoring: 'monitor running',
  error: 'flash failed',
};

function Line({ line }: { line: TerminalLine }) {
  return (
    <div className={`terminal-line level-${line.level}`}>
      <span className="line-ts">{line.ts}</span>
      {line.text}
    </div>
  );
}

export function Terminal() {
  const lines = useStore((s) => s.lines);
  const activeTab = useStore((s) => s.activeTab);
  const setActiveTab = useStore((s) => s.setActiveTab);
  const clearLines = useStore((s) => s.clearLines);
  const collapsed = useStore((s) => s.terminalCollapsed);
  const toggleTerminal = useStore((s) => s.toggleTerminal);
  const flashPhase = useStore((s) => s.flashPhase);
  const flashProgress = useStore((s) => s.flashProgress);
  const serialState = useStore((s) => s.serialState);
  const setSerialState = useStore((s) => s.setSerialState);
  const pushLine = useStore((s) => s.pushLine);
  const serialBaud = useStore((s) => s.settings.serialBaud);
  const setSetting = useStore((s) => s.setSetting);
  const projectName = useStore((s) => s.projectName);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(true);
  const [serialInput, setSerialInput] = useState('');
  const [newlineMode, setNewlineMode] = useState<'\n' | '\r' | '\r\n' | 'none'>('\n');

  const serialLines = lines.filter((l) => l.level === 'serial');
  const outputLines = lines.filter((l) => l.level !== 'serial');
  const visible = activeTab === 'serial' ? serialLines : outputLines;

  useEffect(() => {
    const body = bodyRef.current;
    if (body && pinned) body.scrollTop = body.scrollHeight;
  }, [visible.length, pinned, collapsed]);

  function onScroll(): void {
    const body = bodyRef.current;
    if (!body) return;
    const atBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 24;
    setPinned(atBottom);
  }

  async function onConnectMonitor(): Promise<void> {
    try {
      setSerialState('connecting');
      await startSerialMonitor(serialBaud, (line) => pushLine('serial', line));
      setSerialState('connected');
      pushLine('ok', `Serial monitor open at ${serialBaud} baud.`);
    } catch (err) {
      setSerialState('disconnected');
      pushLine('err', err instanceof Error ? err.message : 'Could not open the serial monitor.');
    }
  }

  async function onDisconnectMonitor(): Promise<void> {
    await stopSerialMonitor();
    setSerialState('disconnected');
    pushLine('info', 'Serial monitor closed.');
  }

  function onSendSerial(): void {
    const session = getSerialSession();
    if (!session || !session.isOpen) {
      pushLine('warn', 'Open the serial monitor first.');
      return;
    }
    void session.writeLine(serialInput, newlineMode);
    setSerialInput('');
  }

  const showFlashProgress =
    !collapsed && flashPhase === 'flashing' && flashProgress !== null;

  return (
    <section className={`terminal-area terminal ${collapsed ? 'is-collapsed' : ''}`} aria-label="Output terminal">
      <div className="terminal-head">
        <button
          className={`terminal-tab ${activeTab === 'output' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveTab('output');
            if (collapsed) toggleTerminal();
          }}
        >
          Output
        </button>
        <button
          className={`terminal-tab ${activeTab === 'serial' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveTab('serial');
            if (collapsed) toggleTerminal();
          }}
        >
          Serial
        </button>

        <div className="terminal-status">
          <span>{projectName}</span>
          <span>·</span>
          <span className={flashPhase === 'error' ? 'state-err' : flashPhase === 'monitoring' ? 'state-ok' : ''}>
            {PHASE_LABELS[flashPhase]}
          </span>
          <span>·</span>
          <span className={serialState === 'connected' ? 'state-ok' : ''}>serial {serialState}</span>
        </div>

        <div className="terminal-tools">
          {!pinned && !collapsed && (
            <button
              className="btn"
              onClick={() => {
                setPinned(true);
                const body = bodyRef.current;
                if (body) body.scrollTop = body.scrollHeight;
              }}
              title="Jump to the latest line"
            >
              <IconChevronDown width={13} height={13} />
              Follow
            </button>
          )}
          <button className="btn" onClick={clearLines} title="Clear the terminal">
            <IconTrash width={13} height={13} />
            Clear
          </button>
          <button className="btn btn-icon" onClick={toggleTerminal} title={collapsed ? 'Expand terminal' : 'Collapse terminal'}>
            <IconTerminal width={13} height={13} />
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {showFlashProgress && (
            <div className="terminal-progress">
              <span>writing flash images</span>
              <div className="terminal-progress-bar">
                <span style={{ width: `${Math.round((flashProgress ?? 0) * 100)}%` }} />
              </div>
              <span>{Math.round((flashProgress ?? 0) * 100)}%</span>
            </div>
          )}

          <div className="terminal-body" ref={bodyRef} onScroll={onScroll}>
            {visible.map((line) => (
              <Line key={line.id} line={line} />
            ))}
            {visible.length === 0 && (
              <div className="terminal-line" style={{ color: '#5d6b7c' }}>
                {activeTab === 'serial'
                  ? 'Serial output appears here once a monitor session is open.'
                  : 'Compile and flash logs appear here.'}
              </div>
            )}
          </div>

          {activeTab === 'serial' && (
            <div className="terminal-inputrow">
              {serialState === 'connected' ? (
                <button className="btn" onClick={() => void onDisconnectMonitor()}>
                  Disconnect
                </button>
              ) : (
                <button className="btn" onClick={() => void onConnectMonitor()}>
                  Open monitor
                </button>
              )}
              <select
                value={serialBaud}
                onChange={(e) => setSetting('serialBaud', Number(e.target.value))}
                aria-label="Serial baud rate"
              >
                {SERIAL_BAUD_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {b} baud
                  </option>
                ))}
              </select>
              <input
                value={serialInput}
                placeholder="Send a line to the board…"
                onChange={(e) => setSerialInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSendSerial();
                }}
              />
              <select
                value={newlineMode}
                onChange={(e) => setNewlineMode(e.target.value as typeof newlineMode)}
                aria-label="Line ending"
              >
                <option value="\n">LF</option>
                <option value="\r">CR</option>
                <option value="\r\n">CRLF</option>
                <option value="none">None</option>
              </select>
              <button className="btn" onClick={onSendSerial}>
                Send
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
